import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogsTable } from '../components/LogsTable';
import { Pagination } from '../components/Pagination';
import { SkeletonTable, SkeletonCard } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { logsAPI } from '../lib/api';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function LogsPage() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const logsPerPage = 10;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterActive]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: logsPerPage,
      };

      if (filterActive && startDate) params.start_date = startDate;
      if (filterActive && endDate) params.end_date = endDate;

      const response = await logsAPI.getLogs(params);
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Gagal memuat log');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    setFilterActive(true);
  };

  const handleFilterClear = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setFilterActive(false);
  };

  const handleDeleteLogs = async () => {
    if (!startDate || !endDate) {
      toast.error('Pilih tanggal mulai dan akhir');
      return;
    }

    setDeleting(true);
    try {
      await logsAPI.deleteLogs(startDate, endDate);
      toast.success('Log berhasil dihapus');
      setDeleteDialogOpen(false);
      setCurrentPage(1);
      loadLogs();
    } catch (error) {
      toast.error('Gagal menghapus log');
    } finally {
      setDeleting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-4 w-64 bg-muted animate-pulse rounded" />
        </div>
        <SkeletonCard />
        <SkeletonTable />
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants}>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Log Aktivitas</h2>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Riwayat aktivitas sistem monitoring
        </p>
      </motion.div>

      {/* Filter Card */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Filter Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <Label htmlFor="start-date" className="text-sm">Tanggal Mulai</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full"
                  />
                </motion.div>
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  <Label htmlFor="end-date" className="text-sm">Tanggal Akhir</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full"
                  />
                </motion.div>
              </div>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    onClick={handleFilterApply}
                    disabled={!startDate && !endDate}
                    className="w-full sm:w-auto"
                  >
                    Saring
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    onClick={handleFilterClear}
                    className="w-full sm:w-auto"
                  >
                    Hapus Filter
                  </Button>
                </motion.div>
                <motion.div 
                  className="sm:ml-auto"
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={!startDate || !endDate}
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Hapus Log
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Logs Table */}
      <motion.div variants={itemVariants}>
        <LogsTable logs={logs} />
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div 
          variants={itemVariants}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </motion.div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md mx-4 sm:mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Log Aktivitas</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus semua log dari{' '}
              <strong>{startDate}</strong> sampai <strong>{endDate}</strong>?
              <br /><br />
              <span className="text-red-600 font-semibold">
                ⚠️ Tindakan ini tidak dapat dibatalkan!
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <motion.div 
              className="w-full sm:w-auto"
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
            >
              <AlertDialogCancel className="w-full">Batal</AlertDialogCancel>
            </motion.div>
            <motion.div 
              className="w-full sm:w-auto"
              whileHover={{ scale: 1.02 }} 
              whileTap={{ scale: 0.98 }}
            >
              <AlertDialogAction 
                onClick={handleDeleteLogs}
                disabled={deleting}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {deleting ? (
                  <motion.span
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    Menghapus...
                  </motion.span>
                ) : (
                  'Ya, Hapus Log'
                )}
              </AlertDialogAction>
            </motion.div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}